import React, {
  FormEventHandler,
  DetailedHTMLProps,
  FormHTMLAttributes,
  FormEvent,
  forwardRef,
} from 'react';

type DefaultFormProps = DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>
type OmittedProps = Pick<DefaultFormProps, 'noValidate' | 'onSubmit' | 'ref'>;

export type FormSubmitHandler = (e: FormEvent<HTMLFormElement>, isValid: boolean) => void;

export type FormProps = Omit<DefaultFormProps, keyof OmittedProps> & {
  autoScrollToError?: boolean;
  onSubmit: FormSubmitHandler;
};

/**
 * Wraps the native `form` element and makes it easy to hook into the native constraint API.
 * Will call 'checkValidity' on all child form fields and pass the results of this check to the parent via 'onSubmit'
 */
export const Form = forwardRef<HTMLFormElement, FormProps>(function Form(props, ref) {
  const { children, autoScrollToError, onSubmit, ...passThroughProps } = props;

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault(); // Prevent a full page refresh

    const form = event.target as HTMLFormElement;

    // If any field values are invalid, checkValidity fires an invalid event on that element
    const isValid = form.checkValidity();

    if (onSubmit) onSubmit(event, isValid);

    // Scroll to the first error if autoScrollToError enabled
    if (!isValid && autoScrollToError) {
      for (let i = 0; i <= form.elements.length; i++) {
        const formElm = form.elements.item(i);
        if (formElm && !(formElm as HTMLInputElement)?.validity?.valid) {
          try {
            formElm.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (e) {
            // Fail gracefully for browsers that don't support scrollIntoView
            console.warn(e);
          }
          break; // We already scrolled - don't try again
        }
      }
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <form
      {...passThroughProps}
      role="form"
      ref={ref}
      onSubmit={handleSubmit}
      noValidate // disabling HTML5 validations by default and access Constraint API programmatically
    >
      {children}
    </form>
  );
});
